import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { AppLayout } from 'components/AppLayout'
import { Button } from 'components/Button'
import {
  formatResultCount,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementSelectFilter,
} from 'components/ManagementFilters'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { MANAGEMENT_PAGE_SIZE } from 'constants/index'
import { routes } from 'constants/routes'
import { commonStrings, accountManagementStrings } from 'constants/strings'
import {
  useAdminAccounts,
  useAdminOrganizations,
  useClientPagination,
  useCurrentUser,
} from 'hooks'
import { useTermsMemberStatuses } from 'hooks/useTermsAdmin'
import { EOrganizationMemberRole, EOrganizationMemberStatus } from 'types'
import type { TTermsMemberConsentState } from 'types/terms'
import { matchesSearch } from 'utils'
import { AccountsTable } from './AccountsTable'
import { AccountsCardList } from './AccountsCardList'

type AccountManagementLocationState = {
  accountCreated?: boolean
  snackbarMessage?: string
}

const roleFilterOptions = [
  { value: EOrganizationMemberRole.ADMIN, label: EOrganizationMemberRole.ADMIN },
  {
    value: EOrganizationMemberRole.STANDARD,
    label: EOrganizationMemberRole.STANDARD,
  },
]

const statusFilterOptions = [
  {
    value: EOrganizationMemberStatus.ACTIVE,
    label: EOrganizationMemberStatus.ACTIVE,
  },
  {
    value: EOrganizationMemberStatus.PAUSED,
    label: EOrganizationMemberStatus.PAUSED,
  },
]

export default function AccountManagement() {
  const navigate = useNavigate()
  const location = useLocation()
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState<EOrganizationMemberRole | ''>('')
  const [statusFilter, setStatusFilter] = useState<
    EOrganizationMemberStatus | ''
  >('')
  const { data: currentUser } = useCurrentUser()
  const canManageAccounts = currentUser?.role === EOrganizationMemberRole.ADMIN
  const { accounts, loading, error } = useAdminAccounts()
  const { data: termsStatuses } = useTermsMemberStatuses()
  const termsStateByAccountId = useMemo(
    () =>
      (termsStatuses ?? []).reduce<Record<number, TTermsMemberConsentState>>(
        (acc, status) => {
          acc[status.accountId] = status.state
          return acc
        },
        {},
      ),
    [termsStatuses],
  )
  const {
    organizations,
    loading: isOrganizationsLoading,
    error: organizationsError,
  } = useAdminOrganizations()
  const organizationFilterOptions = useMemo(
    () =>
      organizations.map((organization) => ({
        value: String(organization.id),
        label: organization.name,
      })),
    [organizations],
  )
  const filteredAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        const organizationIds = account.memberships.map(
          (membership) => membership.organizationId,
        )
        const organizationNames = account.memberships.map(
          (membership) => membership.organizationName,
        )

        if (
          !matchesSearch(
            [
              account.id,
              ...organizationIds,
              ...organizationNames,
              account.name,
              account.email,
            ],
            searchKeyword,
          )
        ) {
          return false
        }

        if (
          organizationFilter &&
          !organizationIds.some(
            (organizationId) => String(organizationId) === organizationFilter,
          )
        ) {
          return false
        }

        if (roleFilter && account.role !== roleFilter) {
          return false
        }

        return !(statusFilter && account.status !== statusFilter)
      }),
    [accounts, organizationFilter, roleFilter, searchKeyword, statusFilter],
  )
  const isFiltering = Boolean(
    searchKeyword || organizationFilter || roleFilter || statusFilter,
  )
  const countLabel = formatResultCount(filteredAccounts.length, accounts.length)
  const paginationResetKey = [
    searchKeyword,
    organizationFilter,
    roleFilter,
    statusFilter,
  ].join('\u0000')
  const {
    currentPage,
    totalPage,
    pageItems: paginatedAccounts,
    goPrev,
    goNext,
  } = useClientPagination(
    filteredAccounts,
    MANAGEMENT_PAGE_SIZE,
    paginationResetKey,
  )
  const emptyMessage = isFiltering
    ? commonStrings.filteredEmpty
    : accountManagementStrings.states.empty
  const viewError = error || organizationsError

  useEffect(() => {
    const state = location.state as AccountManagementLocationState | null
    const message =
      state?.snackbarMessage ||
      (state?.accountCreated
        ? accountManagementStrings.messages.createSuccess
        : undefined)
    if (!message) return

    setSnackbarMessage(message)
    setIsSnackbarOpen(true)
    navigate(routes.ACCOUNT_MANAGEMENT, { replace: true, state: null })
  }, [
    location.state,
    navigate,
  ])

  return (
    <AppLayout>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {accountManagementStrings.title}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            {canManageAccounts && (
              <Button
                type="button"
                size="sm"
                onClick={() => navigate(routes.ACCOUNT_MANAGEMENT_CREATE)}
              >
                {accountManagementStrings.actions.createAccount}
              </Button>
            )}
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={currentPage}
              totalPage={totalPage}
              loading={loading || isOrganizationsLoading}
              onPrevious={goPrev}
              onNext={goNext}
            />
          </div>
        </header>

        {viewError && (
          <div className="mt-6">
            <Alert>{viewError}</Alert>
          </div>
        )}

        <ManagementFilterBar>
          <ManagementSearchInput
            label={commonStrings.filters.searchLabel}
            placeholder={accountManagementStrings.filters.searchPlaceholder}
            value={searchKeyword}
            onChange={setSearchKeyword}
            onClear={() => setSearchKeyword('')}
          />
          <ManagementSelectFilter
            label={accountManagementStrings.filters.organization}
            value={organizationFilter}
            options={organizationFilterOptions}
            onChange={setOrganizationFilter}
            onClear={() => setOrganizationFilter('')}
          />
          <ManagementSelectFilter
            label={accountManagementStrings.filters.role}
            value={roleFilter}
            options={roleFilterOptions}
            onChange={setRoleFilter}
            onClear={() => setRoleFilter('')}
          />
          <ManagementSelectFilter
            label={accountManagementStrings.filters.status}
            value={statusFilter}
            options={statusFilterOptions}
            onChange={setStatusFilter}
            onClear={() => setStatusFilter('')}
          />
        </ManagementFilterBar>

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
          <AccountsTable
            accounts={paginatedAccounts}
            loading={loading || isOrganizationsLoading}
            emptyMessage={emptyMessage}
            termsStateByAccountId={termsStateByAccountId}
            canEdit={canManageAccounts}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <AccountsCardList
            accounts={paginatedAccounts}
            loading={loading || isOrganizationsLoading}
            emptyMessage={emptyMessage}
            termsStateByAccountId={termsStateByAccountId}
            canEdit={canManageAccounts}
            className="md:grid-cols-2"
          />
        </div>
      </section>
      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </AppLayout>
  )
}
